import { createReducer } from '@reduxjs/toolkit';

import {
    FireTrailManager,
    FuelManagementRegisterManager,
    Loadable,
} from 'models';
import { makeThunkFromAPICall } from 'state';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
export type FireTrailState = {
    addFireTrailResponse: Loadable<FireTrailManager.ActionResponse>;
    deleteFireTrailResponse: Loadable<FireTrailManager.ActionResponse>;
    deleteBulkFireTrailResponse: Loadable<FireTrailManager.ActionResponse>;
    list: Loadable<FireTrailManager.FireTrail[]>;
    audit: Loadable<FireTrailManager.FireTrailAuditRecord[]>;
    selectedFeature: Loadable<Feature<Geometry>>;
    extent: Loadable<FireTrailManager.API.Extent>;
    uploads: Loadable<FuelManagementRegisterManager.DataUpload[]>;
};

const initialState: FireTrailState = {
    addFireTrailResponse: {
        status: 'idle',
    },
    deleteFireTrailResponse: {
        status: 'idle',
    },
    list: {
        status: 'idle',
    },
    audit: {
        status: 'idle',
    },
    selectedFeature: {
        status: 'idle',
    },
    uploads: {
        status: 'idle',
    },
    extent: {
        status: 'idle',
    },
    deleteBulkFireTrailResponse: {
        status: 'idle',
    },
};

export const [sendFireTrailAdd, setAddFireTrailResponse] = makeThunkFromAPICall(
    FireTrailManager.addFireTrail,
    'firetrail/add',
);

export const [sendFireTrailUpdate, setUpdateFireTrailResponse] =
    makeThunkFromAPICall(FireTrailManager.updateFireTrail, 'firetrail/update');

export const [sendBulkFireTrailUpdate, setBulkUpdateFireTrailResponse] =
    makeThunkFromAPICall(
        FireTrailManager.bulkEditFireTrails,
        'firetrail/bulk/update',
    );

export const [sendBulkFireTrailDelete, setBulkFireTrailDelete] =
    makeThunkFromAPICall(
        FireTrailManager.bulkDeleteFireTrails,
        'firetrail/bulk/delete',
    );

export const [sendFireTrailDelete, setDeleteFireTrailResponse] =
    makeThunkFromAPICall(FireTrailManager.deleteFireTrail, 'firetrail/delete');

export const [getFireTrails, setFireTrails] = makeThunkFromAPICall(
    FireTrailManager.getFireTrailList,
    'firetrail/list',
);

export const [getFireTrailAudit, setFireTrailAudit] = makeThunkFromAPICall(
    FireTrailManager.getFireTrailAudit,
    'firetrail/audit',
);

export const [getFireTrailExtent, setFireTrailExtent] = makeThunkFromAPICall(
    FireTrailManager.getFireTrailExtent,
    'firetrail/extent',
);

export const [getAggregateFireTrails, setAggregateFireTrails] =
    makeThunkFromAPICall(
        FireTrailManager.getAggregateFireTrails,
        'firetrail/aggregate/list',
    );

export const [getFireTrailSuggestions, setFireTrailSuggestions] =
    makeThunkFromAPICall(
        FireTrailManager.getFireTrailSuggestions,
        'firetrail/suggestions',
    );

export const [
    getSelectedFeatureFromGeoserver,
    setSelectedFeatureFromGeoserver,
] = makeThunkFromAPICall(
    FireTrailManager.getSelectedFeatureFromGeoserver,
    'firetrail/selected/fmr',
);

export const [importDataToFireTrail, setImportDataToFireTrail] =
    makeThunkFromAPICall(
        FireTrailManager.importDataToFireTrail,
        'firetrail/import/data',
    );

export const [uploadFireTrail] = makeThunkFromAPICall(
    FireTrailManager.uploadFireTrail,
    'firetrail/data-upload/fmr',
);

export const [getFireTrailUploads, setFireTrailUploads] = makeThunkFromAPICall(
    FireTrailManager.getFireTrailUploads,
    'firetrail/data-upload/uploads',
);

export const firetrailReducer = createReducer(initialState, (builder) => {
    builder.addCase(setAddFireTrailResponse, (state, action) => {
        return {
            ...state,
            addFireTrailResponse: {
                ...state.addFireTrailResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setDeleteFireTrailResponse, (state, action) => {
        return {
            ...state,
            deleteFireTrailResponse: {
                ...state.deleteFireTrailResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setBulkFireTrailDelete, (state, action) => {
        return {
            ...state,
            deleteBulkFireTrailResponse: {
                ...state.deleteBulkFireTrailResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setFireTrails, (state, action) => {
        return {
            ...state,
            list: { ...state.list, ...action.payload.loadable },
        };
    });

    builder.addCase(setFireTrailAudit, (state, action) => {
        return {
            ...state,
            audit: { ...state.audit, ...action.payload.loadable },
        };
    });

    builder.addCase(setSelectedFeatureFromGeoserver, (state, action) => {
        return {
            ...state,
            selectedFeature: {
                ...state.selectedFeature,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setFireTrailUploads, (state, action) => {
        return {
            ...state,
            uploads: {
                ...state.uploads,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setFireTrailExtent, (state, action) => {
        return {
            ...state,
            extent: {
                ...state.extent,
                ...action.payload.loadable,
            },
        };
    });
});

export const FireTrailActions = {
    getFireTrailSuggestions,
    getFireTrails,
    getFireTrailExtent,
    getSelectedFeatureFromGeoserver,
    getFireTrailAudit,
    sendFireTrailAdd,
    sendFireTrailUpdate,
    sendFireTrailDelete,
    importDataToFireTrail,
    getFireTrailUploads,
    uploadFireTrail,
    getAggregateFireTrails,
    sendBulkFireTrailUpdate,
    sendBulkFireTrailDelete,
};

export default firetrailReducer;
